/**
 * PresentationContext — the single source of truth for a presentation's mode,
 * identity, and write callbacks (Round 10).
 *
 * One context drives every component. There is NO separate "present" vs "edit"
 * component (CLAUDE.md "one component per node type"); leaves read `useEditable()`
 * and branch internally.
 *
 *   mode:      'edit' | 'present' | 'client'
 *   editable:  true only in edit mode (team)
 *   reactions: true only when a journey token is present (client, or team preview)
 *   token:     journey token (client mode) or null
 *   identity:  { name, clerkUserId } for the team, or { client:true } for clients
 *   onPatch(fields):                 write presentation-record fields (edit only)
 *   onChildWrite(kind, payload):     write a child record (edit only)
 */

import { createContext, useContext } from 'react'

const PresentationContext = createContext({
  mode: 'present',
  editable: false,
  reactions: false,
  token: null,
  identity: null,
  presentation: null,
  recordId: null,                  // Project Concepts record id (edit writes)
  editHistory: {},                 // { editKey: originalUrl } for image revert
  editHistoryField: 'Image Edit History',  // which Airtable JSON field stores it (per type)
  adjustMap: {},                   // { editKey: { posX, posY, scale } } image framing
  onAdjust: () => {},              // (editKey, adjust|null) => void
  reload: () => {},                // re-fetch the presentation after an edit
  reactionMap: {},                 // { reactRef: gesture }
  onReact: async () => {},         // (reactRef, gesture, meta) => void
  onPatch: async () => {},
  onChildWrite: async () => {},
  // Image map — Supabase-backed slot overrides. Shape: { [slotKey]: { url, storagePath } }.
  // url='' is a tombstone (slot intentionally cleared; wins over Airtable fallback).
  imageMap: {},
  onImageUpsert: async () => {},   // (slotKey, url, storagePath) => void — optimistic update + persist
  onImageSwap: async () => {},     // (slotKeyA, urlA, pathA, slotKeyB, urlB, pathB) => void
  onImageDelete: async () => {},   // (slotKey) => void — tombstone write
  // Which image slot (slotKey) is currently open in the focused reframe editor, or null.
  // Shared so drag handles / other chrome can hide while one frame is being reframed.
  reframeKey: null,
  setReframeKey: () => {},
  // Media Library (the pool): explicit rows added to the concept. The drawer unions
  // these with on-page + Airtable-base images. onLibraryAdd is called from the drawer
  // AND from the per-frame Replace/AI editor so every uploaded image joins the pool.
  library: [],                     // [{ id, url, storagePath, source }]
  onLibraryAdd: async () => {},    // (url, storagePath, source) => void
  onLibraryDelete: async () => {}, // (id) => void
  // Per-section chosen image-grid layout. { [sectionKey]: layoutId }. Absent = the section's
  // built-in default. Flows to the client render read-only; only edit mode writes.
  layouts: {},
  onSetLayout: async () => {},     // (sectionKey, layoutId) => void
})

export function PresentationProvider({ value, children }) {
  return <PresentationContext.Provider value={value}>{children}</PresentationContext.Provider>
}

export function usePresentation() { return useContext(PresentationContext) }
export function useEditable() { return useContext(PresentationContext).editable }
export function useReactionsEnabled() { return useContext(PresentationContext).reactions }

/** Current framing adjust + setter for a given image edit key. */
export function useAdjust(key) {
  const ctx = useContext(PresentationContext)
  const adjust = ctx.adjustMap ? ctx.adjustMap[key] : undefined
  const set = (a) => ctx.onAdjust && ctx.onAdjust(key, a)
  return [adjust, set]
}

/** Current gesture + setter for a given reaction ref. */
export function useReaction(reactRef) {
  const ctx = useContext(PresentationContext)
  const gesture = ctx.reactionMap ? ctx.reactionMap[reactRef] : undefined
  const set = (g, meta) => ctx.onReact && ctx.onReact(reactRef, g, meta)
  return [gesture, set]
}

export default PresentationContext
